/**
 * Live explore replay map: densified polylines from streaming catalog + playhead.
 */

import { getInterpolatedPointAtTime, type InterpolateOptions } from "./trackInterpolation";

export type LiveReplayCoordReader = (d: unknown) => { lat: number; lng: number } | null;
export type LiveReplayTimestampReader = (d: unknown) => number;

/** Default vertex spacing for live replay tracks (200ms = 5 Hz display grid). */
export const LIVE_REPLAY_TRACK_STEP_MS = 200;

/** Same coords as last point (within ~1 m). */
export function liveReplayPointsSameLocation(
  a: unknown,
  b: unknown,
  readCoords: LiveReplayCoordReader
): boolean {
  const ca = readCoords(a);
  const cb = readCoords(b);
  if (!ca || !cb) return false;
  const dLat = ca.lat - cb.lat;
  const dLng = ca.lng - cb.lng;
  return dLat * dLat + dLng * dLng < 1e-10;
}

/**
 * Build a replay track polyline by interpolating the catalog every stepMs from
 * max(windowStart, first catalog sample) through playhead.
 */
export function buildDensifiedReplayTrack(
  catalog: unknown[],
  windowStartMs: number,
  playheadMs: number,
  stepMs: number,
  readTimestamp: LiveReplayTimestampReader,
  readCoords: LiveReplayCoordReader,
  interpolateOptions: InterpolateOptions
): unknown[] {
  if (!catalog?.length || !Number.isFinite(playheadMs)) return [];

  const step = Math.max(50, Math.floor(stepMs));
  const firstCatalogMs = readTimestamp(catalog[0]);
  if (!Number.isFinite(firstCatalogMs)) return [];

  const startMs = Math.max(windowStartMs, firstCatalogMs);
  if (playheadMs < startMs) return [];

  const samples: unknown[] = [];
  for (let t = startMs; t <= playheadMs; t += step) {
    const pt = getInterpolatedPointAtTime(catalog, new Date(t), interpolateOptions);
    if (!pt || readCoords(pt) === null) continue;
    samples.push({
      ...(pt as object),
      timestamp: t,
      Datetime: new Date(t),
      __renderLat: readCoords(pt)!.lat,
      __renderLng: readCoords(pt)!.lng,
    });
  }

  const lastSampleTs = samples.length > 0 ? readTimestamp(samples[samples.length - 1]) : 0;
  if (lastSampleTs < playheadMs) {
    const pt = getInterpolatedPointAtTime(catalog, new Date(playheadMs), interpolateOptions);
    if (pt && readCoords(pt) !== null) {
      if (
        samples.length > 0 &&
        liveReplayPointsSameLocation(samples[samples.length - 1], pt, readCoords)
      ) {
        samples[samples.length - 1] = {
          ...(pt as object),
          timestamp: playheadMs,
          Datetime: new Date(playheadMs),
          __renderLat: readCoords(pt)!.lat,
          __renderLng: readCoords(pt)!.lng,
        };
      } else {
        samples.push({
          ...(pt as object),
          timestamp: playheadMs,
          Datetime: new Date(playheadMs),
          __renderLat: readCoords(pt)!.lat,
          __renderLng: readCoords(pt)!.lng,
        });
      }
    }
  }

  return samples.filter((p) => readTimestamp(p) >= windowStartMs);
}

/**
 * Raw replay track: all catalog samples in [windowStart, playhead] with valid coords.
 * Sorted by timestamp — required for D3 linear polylines (unsorted catalog = fan/spokes).
 */
export function sliceReplayRawTrack(
  catalog: unknown[],
  windowStartMs: number,
  playheadMs: number,
  readTimestamp: LiveReplayTimestampReader,
  readCoords: LiveReplayCoordReader
): unknown[] {
  if (!catalog?.length || !Number.isFinite(playheadMs)) return [];

  const inWindow = catalog.filter((p) => {
    const ts = readTimestamp(p);
    return ts > 0 && ts >= windowStartMs && ts <= playheadMs && readCoords(p) !== null;
  });

  inWindow.sort((a, b) => readTimestamp(a) - readTimestamp(b));

  // Freeze lat/lng at slice time so D3 never reads stale alias fields on later frames
  return inWindow.map((p) => {
    const c = readCoords(p)!;
    return { ...(p as object), __renderLat: c.lat, __renderLng: c.lng };
  });
}

export type ReplayTrailAcceptPoint = (
  trail: unknown[],
  point: unknown,
  pointTs: number
) => boolean;

/**
 * Append new catalog samples to an existing replay trail (append-only ingest).
 * Assumes catalog is roughly time-sorted; stops at first sample after playheadMs.
 */
export function appendCatalogSamplesToReplayTrail(
  trail: unknown[],
  catalog: unknown[],
  catalogStartIndex: number,
  playheadMs: number,
  windowStartMs: number,
  readTimestamp: LiveReplayTimestampReader,
  readCoords: LiveReplayCoordReader,
  acceptPoint?: ReplayTrailAcceptPoint
): { trail: unknown[]; nextCatalogIndex: number } {
  let updated = trail;
  let nextIndex = Math.max(0, catalogStartIndex);

  for (let i = nextIndex; i < catalog.length; i++) {
    const point = catalog[i];
    const ts = readTimestamp(point);
    if (!Number.isFinite(ts) || ts <= 0) {
      nextIndex = i + 1;
      continue;
    }
    if (ts > playheadMs) {
      break;
    }
    if (ts < windowStartMs) {
      nextIndex = i + 1;
      continue;
    }
    const coords = readCoords(point);
    if (!coords) {
      nextIndex = i + 1;
      continue;
    }

    const accepted = acceptPoint ? acceptPoint(updated, point, ts) : true;
    if (accepted) {
      updated = [
        ...updated,
        {
          ...(point as object),
          __renderLat: coords.lat,
          __renderLng: coords.lng,
        },
      ];
    }
    nextIndex = i + 1;
  }

  updated = updated.filter((p) => readTimestamp(p) >= windowStartMs);
  return { trail: updated, nextCatalogIndex: nextIndex };
}

/** Drop trail vertices older than the rolling window start. */
export function pruneReplayTrailToWindow(
  trail: unknown[],
  windowStartMs: number,
  readTimestamp: LiveReplayTimestampReader
): unknown[] {
  return trail.filter((p) => readTimestamp(p) >= windowStartMs);
}

/** Stable fingerprint for incremental replay trail renders. */
export function replayTrailRenderSignature(
  sourceIds: Iterable<number>,
  trailsBySource: Map<number, unknown[]>,
  readTimestamp: LiveReplayTimestampReader
): string {
  const parts: string[] = [];
  for (const sourceId of [...sourceIds].sort((a, b) => a - b)) {
    const trail = trailsBySource.get(sourceId) ?? [];
    if (trail.length === 0) {
      parts.push(`${sourceId}:0`);
      continue;
    }
    const firstTs = readTimestamp(trail[0]);
    const lastTs = readTimestamp(trail[trail.length - 1]);
    parts.push(`${sourceId}:${trail.length}:${firstTs}:${lastTs}`);
  }
  return parts.join("|");
}

/**
 * Catalog trail fingerprint plus playhead bucket — used so live replay tracks
 * re-densify to the boat clock even when the history catalog is unchanged.
 */
export function replayTrailRenderSignatureWithPlayhead(
  sourceIds: Iterable<number>,
  trailsBySource: Map<number, unknown[]>,
  readTimestamp: LiveReplayTimestampReader,
  playheadMs: number,
  playheadStepMs: number
): string {
  const trailFingerprint = replayTrailRenderSignature(sourceIds, trailsBySource, readTimestamp);
  const bucket =
    Number.isFinite(playheadMs) && playheadStepMs > 0
      ? Math.floor(playheadMs / playheadStepMs)
      : 0;
  return `p${bucket}|${trailFingerprint}`;
}

/** Append one smooth playhead sample to the per-source trail (called each boat clock tick). */
export function appendReplayTrailSample(
  trail: unknown[],
  boatPoint: unknown | null | undefined,
  playheadMs: number,
  readTimestamp: LiveReplayTimestampReader,
  readCoords: LiveReplayCoordReader,
  windowStartMs: number,
  /** Minimum playhead advance before adding another vertex (0 = every boat tick). */
  minSampleIntervalMs = 0
): unknown[] {
  const trimmed = trail.filter((p) => readTimestamp(p) >= windowStartMs);

  if (!boatPoint || readCoords(boatPoint) === null || !Number.isFinite(playheadMs)) {
    return trimmed;
  }

  const last = trimmed.length > 0 ? trimmed[trimmed.length - 1] : null;
  if (last) {
    const lastMs = readTimestamp(last);
    if (playheadMs < lastMs) {
      return trimmed;
    }
    if (playheadMs === lastMs) {
      if (liveReplayPointsSameLocation(last, boatPoint, readCoords)) {
        return trimmed;
      }
      // Same playhead (live cap stall) but boat moved — update tip in place
      const sample = {
        ...(boatPoint as object),
        timestamp: playheadMs,
        Datetime: new Date(playheadMs),
      };
      return [...trimmed.slice(0, -1), sample];
    } else if (minSampleIntervalMs > 0 && playheadMs - lastMs < minSampleIntervalMs) {
      return trimmed;
    }
  }

  const sample = {
    ...(boatPoint as object),
    timestamp: playheadMs,
    Datetime: new Date(playheadMs),
  };

  return [...trimmed, sample];
}

/**
 * Merge store window samples with smooth trail extension (points after last store sample).
 */
export function mergeStoreBodyWithReplayTrail(
  storePoints: unknown[],
  trail: unknown[],
  playheadMs: number,
  readTimestamp: LiveReplayTimestampReader,
  readCoords: LiveReplayCoordReader
): unknown[] {
  const body = storePoints.filter(
    (p) => readCoords(p) !== null && readTimestamp(p) <= playheadMs
  );

  const trailInWindow = trail.filter(
    (p) => readCoords(p) !== null && readTimestamp(p) <= playheadMs
  );

  if (body.length === 0) {
    return trailInWindow;
  }

  if (trailInWindow.length === 0) {
    return body;
  }

  const lastBodyTs = readTimestamp(body[body.length - 1]);
  const extension = trailInWindow.filter((p) => readTimestamp(p) > lastBodyTs);

  if (extension.length === 0) {
    return body;
  }

  return [...body, ...extension];
}

/** @deprecated Use mergeStoreBodyWithReplayTrail — kept for tests migrating off single-tail. */
export function appendSharedBoatTailToTrack(
  windowPoints: unknown[],
  boatPoint: unknown | null | undefined,
  readTimestamp: LiveReplayTimestampReader,
  readCoords: LiveReplayCoordReader
): unknown[] {
  const body = windowPoints.filter((p) => readCoords(p) !== null);
  if (!boatPoint || readCoords(boatPoint) === null) {
    return body;
  }
  if (body.length === 0) {
    return [boatPoint];
  }
  const lastBody = body[body.length - 1];
  if (liveReplayPointsSameLocation(lastBody, boatPoint, readCoords)) {
    return body;
  }
  const lastTs = readTimestamp(lastBody);
  const tail = {
    ...(boatPoint as object),
    timestamp: lastTs,
    Datetime:
      (lastBody as { Datetime?: Date }).Datetime instanceof Date
        ? (lastBody as { Datetime: Date }).Datetime
        : new Date(lastTs),
  };
  return [...body, tail];
}

export function buildLiveReplayTrackPath(
  windowPoints: unknown[],
  catalogPoints: unknown[],
  playhead: Date,
  interpolateOptions: InterpolateOptions,
  readTimestamp: LiveReplayTimestampReader,
  readCoords: LiveReplayCoordReader
): unknown[] {
  const body = windowPoints.filter((p) => readCoords(p) !== null);
  const playheadMs = playhead.getTime();
  const boatPoint = getInterpolatedPointAtTime(catalogPoints, playhead, interpolateOptions);

  if (!boatPoint || readCoords(boatPoint) === null) {
    return body.filter((p) => readTimestamp(p) <= playheadMs);
  }

  return appendSharedBoatTailToTrack(body, boatPoint, readTimestamp, readCoords);
}
